import { Switch as HeadlessSwitch } from '@headlessui/react';
import { cn } from '@lib/utils/cn';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
}: SwitchProps) {
  const sizes = {
    sm: {
      switch: 'h-5 w-9',
      dot: 'h-4 w-4',
      translate: 'translate-x-4',
    },
    md: {
      switch: 'h-6 w-11',
      dot: 'h-5 w-5',
      translate: 'translate-x-5',
    },
  };

  return (
    <div className="flex items-center gap-3">
      <HeadlessSwitch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={cn(
          'relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          sizes[size].switch,
          checked ? 'bg-primary-600' : 'bg-secondary-300 dark:bg-secondary-600',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none inline-block rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
            sizes[size].dot,
            checked ? sizes[size].translate : 'translate-x-0'
          )}
        />
      </HeadlessSwitch>
      {label && (
        <span
          onClick={() => !disabled && onChange(!checked)}
          className={cn(
            'text-sm font-medium text-secondary-900 dark:text-white cursor-pointer select-none',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
}
